import { useState } from "react";
import { Search, Wallet, Clock, Coins, FileText, Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { SalarySlip } from "@/components/admin/SalarySlip";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const labelFromKey = (key) => {
  if (!key) return "—";
  const [y, m] = key.split("-");
  return m ? `${MONTHS[Number(m) - 1]} ${y}` : key;
};

export default function HostSalaryLookup() {
  const [uid, setUid] = useState("");
  const [records, setRecords] = useState(null);
  const [loading, setLoading] = useState(false);
  const [slip, setSlip] = useState(null);

  const lookup = async (e) => {
    e.preventDefault();
    if (!uid.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get("/host-salary", { params: { user_id: uid.trim() } });
      setRecords(data);
    } catch {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-12">
      <div className="text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Official Host</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold text-white sm:text-5xl">Salary Status Lookup</h1>
        <p className="mx-auto mt-3 max-w-lg text-slate-400">
          Enter your Golden Live User ID to view your monthly salary records and download your salary slip.
        </p>
      </div>

      <form onSubmit={lookup} className="mx-auto mt-8 flex max-w-md gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
          <input value={uid} onChange={(e) => setUid(e.target.value)} placeholder="Your Golden Live User ID"
            data-testid="lookup-userid-input"
            className="h-12 w-full rounded-xl border border-gold/20 bg-obsidian-card pl-12 pr-4 text-slate-100 outline-none focus:border-gold/60" />
        </div>
        <button type="submit" disabled={loading} data-testid="lookup-submit-btn"
          className="flex h-12 items-center gap-2 rounded-xl gold-gradient px-6 font-bold text-obsidian disabled:opacity-60">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Check"}
        </button>
      </form>

      <div className="mt-8 space-y-4">
        {records !== null && records.length === 0 && (
          <div className="card-surface flex flex-col items-center gap-2 rounded-2xl py-12 text-center" data-testid="lookup-empty">
            <ShieldCheck className="h-10 w-10 text-gold/40" />
            <p className="text-slate-400">No salary records found for this User ID.</p>
            <p className="text-xs text-slate-500">Make sure your ID is correct, or contact your agency/admin.</p>
          </div>
        )}
        {(records || []).map((r) => (
          <div key={r.id} className="card-surface rounded-2xl p-5" data-testid={`lookup-record-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-bold text-slate-100">{labelFromKey(r.salary_month)}</p>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.status === "Paid" ? "bg-whatsapp/15 text-whatsapp" : "bg-amber-500/15 text-amber-400"}`}>{r.status}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.target_status === "Completed" ? "bg-whatsapp/15 text-whatsapp" : "bg-slate-600/30 text-slate-400"}`}>{r.target_status}</span>
                </div>
                <p className="mt-1 text-sm text-slate-400">{r.host_name}{r.agency && ` · ${r.agency}`}</p>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-400">
                  <span><Clock className="mr-1 inline h-3 w-3 text-gold" />{r.total_live_hours || "0"}/120 hrs</span>
                  <span><Wallet className="mr-1 inline h-3 w-3 text-gold" />Total: {r.total_salary || "—"}</span>
                  <span><Coins className="mr-1 inline h-3 w-3 text-gold" />{r.total_reward_coins || "0"} coins</span>
                  {r.payment_date && <span>Paid {r.payment_date}</span>}
                </div>
              </div>
              <button onClick={() => setSlip(r)} data-testid={`lookup-slip-${r.id}`}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gold/30 px-3 py-2 text-sm font-bold text-gold hover:bg-gold/10">
                <FileText className="h-4 w-4" /> Slip
              </button>
            </div>
          </div>
        ))}
      </div>

      {slip && <SalarySlip record={slip} onClose={() => setSlip(null)} />}
    </div>
  );
}
